
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useMasterLicenseSettings } from '@/contexts/MasterLicenseSettingsContext';
import { useResellerPlans } from '@/hooks/license/useResellerPlans';
import { ResellerPlanDetails } from '@/hooks/license/resellerPlans/types';
import LicenseDefaultSettings from './general/LicenseDefaultSettings';
import LogoUploader from './general/LogoUploader';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

const GeneralSettingsTab: React.FC = () => {
  const { licenseSettings, handleSettingChange } = useMasterLicenseSettings();
  const { resellerPlans } = useResellerPlans(true); // Use read-only mode to just get the plans
  const [selectedPlan, setSelectedPlan] = useState<ResellerPlanDetails | null>(null);
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // Find the selected plan or use the Reseller Pro plan as default
  useEffect(() => {
    if (resellerPlans.length > 0 && licenseSettings.planId) {
      const plan = resellerPlans.find(plan => plan.id === licenseSettings.planId);
      if (plan) {
        setSelectedPlan(plan);
      } else {
        // If no plan is selected or the selected plan is not found, 
        // try to find Reseller Pro plan as fallback
        const resellerProPlan = resellerPlans.find(plan => plan.name === 'Reseller Pro');
        if (resellerProPlan) {
          setSelectedPlan(resellerProPlan);
          handleSettingChange('planId', resellerProPlan.id);
          handleSettingChange('type', 'reseller');
        }
      }
    }
  }, [resellerPlans, licenseSettings.planId, handleSettingChange]);

  // Handle plan change
  const handlePlanChange = (planId: string) => {
    handleSettingChange('planId', planId);
    handleSettingChange('type', 'reseller');
    
    const newSelectedPlan = resellerPlans.find(plan => plan.id === planId);
    if (newSelectedPlan) {
      setSelectedPlan(newSelectedPlan);
    }
  };

  // Handle logo upload
  const handleLogoUpload = (logoUrl: string) => {
    handleSettingChange('logoUrl', logoUrl);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      toast({
        title: "Settings Saved",
        description: "General settings have been saved successfully."
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader>
          <CardTitle>General Settings</CardTitle>
          <CardDescription>
            Configure general master license portal settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* License Default Settings */}
          <LicenseDefaultSettings
            duration={licenseSettings.duration}
            autoRenewal={licenseSettings.autoRenewal}
            planId={licenseSettings.planId}
            resellerPlans={resellerPlans}
            selectedPlan={selectedPlan}
            onSettingChange={handleSettingChange}
            onPlanChange={handlePlanChange}
          />
          
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">Website Logo</h3>
            <LogoUploader 
              logoUrl={licenseSettings.logoUrl} 
              onLogoUpload={handleLogoUpload} 
            />
          </div>
          
          <Button type="submit" className="mt-4" disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </CardContent>
      </Card>
    </form>
  );
};

export default GeneralSettingsTab;
